import {
  getQueryString,
  handleError,
  page_size,
  toRelativeApiUrl,
} from 'api/apiUtils';
import { createAxiosInstance } from 'api/apiUtils';
import { Member } from 'components/common/ObjectTypes';
import { refreshMyDetails, refreshWorkspaces } from 'api/cacheRefresh';

// Members fetched per request. Deliberately its own constant: the member walk
// below must not change size because someone retunes the filter-dropdown page
// size it happened to match.
const member_page_size = 200;

export type PaginatedMembers = {
  results: Member[];
  // The number of members actually fetched (`results.length`), not the
  // server-side total — the two only differ if the pagination walk was cut
  // short by its loop guards, which is itself a paging bug.
  count: number;
};

export async function getOrganizationsList(token: string, query: object) {
  const axiosInstance = await createAxiosInstance(token);
  const query_string = getQueryString({ limit: page_size, ...query });
  try {
    const resp = await axiosInstance.get(
      '/v2/organizations/?pager=limit&' + query_string,
    );
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

export async function getOrganizationDetails(token: string, org_id: string) {
  const axiosInstance = await createAxiosInstance(token);
  try {
    const resp = await axiosInstance.get('/v2/organizations/' + org_id);
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

// Bounds the pagination walk below. `member_page_size` (200) members per page
// means this covers 20k members; hitting it means something is wrong with the
// paging rather than that a workspace is genuinely that large.
const MAX_MEMBER_PAGES = 100;

/**
 * Returns every member of a workspace.
 *
 * The endpoint exposes two pagers. Its default is a cursor pager that ignores
 * `limit`/`page_size`, caps pages at 20 and omits `count` entirely. Passing
 * `pager=limit` (the same convention `getOrganizationsList` uses) switches it
 * to limit/offset, which honors `limit` and returns `count` — so a typical
 * workspace comes back in a single request.
 *
 * The `next` walk remains for workspaces larger than one page. Without it only
 * the first page was ever returned, which truncated the members table and made
 * admin detection miss anyone past page 1.
 */
export async function getOrganizationMembers(
  token: string,
  org_id: string,
): Promise<PaginatedMembers> {
  const axiosInstance = await createAxiosInstance(token);
  try {
    let url: string | null =
      '/v2/organizations/' +
      org_id +
      '/members/?pager=limit&' +
      getQueryString({ limit: member_page_size });
    const results: Member[] = [];
    // A `next` that fails to advance would otherwise loop forever, holding
    // the members table and every admin-gated control unresolved.
    const visited = new Set<string>();
    while (url && !visited.has(url) && visited.size < MAX_MEMBER_PAGES) {
      visited.add(url);
      const resp = await axiosInstance.get(url);
      results.push(...(resp.data?.results ?? []));
      url = toRelativeApiUrl(resp.data?.next);
    }
    if (url) {
      // eslint-disable-next-line no-console
      console.warn(
        `Stopped paginating workspace members after ${visited.size} pages; the list may be incomplete.`,
      );
    }
    return { results, count: results.length };
  } catch (err) {
    // Rethrows everything except 401, which it swallows because the session
    // -expired modal is already handling it. Return the empty shape for that
    // one case so callers never see undefined where they expect `results`.
    handleError(err);
    return { results: [], count: 0 };
  }
}

export async function inviteOrganizationMember(
  token: string,
  org_id: string,
  member: Object,
) {
  const axiosInstance = await createAxiosInstance(token);
  try {
    const resp = await axiosInstance.put(
      '/v2/organizations/' + org_id + '/members/invite/',
      member,
    );
    // The workspace's member list lives under the same shared key.
    refreshWorkspaces();
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

export async function removeOrganizationMember(
  token: string,
  org_id: string,
  member_id: string,
  member: Object,
) {
  const axiosInstance = await createAxiosInstance(token);
  try {
    const resp = await axiosInstance.post(
      '/v2/organizations/' + org_id + '/members/remove/',
      { member_ids: [member_id] },
    );
    // Removing yourself takes the workspace off your list and your profile.
    refreshWorkspaces();
    refreshMyDetails();
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

export async function addOrganization(token: string, organization: Object) {
  const axiosInstance = await createAxiosInstance(token);
  try {
    const resp = await axiosInstance.post('/v2/organizations/', organization);
    // Your profile lists your workspaces too.
    refreshWorkspaces();
    refreshMyDetails();
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

export async function updateOrganization(
  token: string,
  org_id: string,
  organization: Object,
) {
  const axiosInstance = await createAxiosInstance(token);
  try {
    const resp = await axiosInstance.patch(
      '/v2/organizations/' + org_id + '/',
      organization,
    );
    // Your profile lists your workspaces too.
    refreshWorkspaces();
    refreshMyDetails();
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}
